import React from "react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, UserPlus, Users } from "lucide-react";

export default function MemberTagSelector({
  users = [],
  selectedUserIds = [],
  onChange,
  disabled = false,
  label = "Tagged Members",
  placeholder = "Tag a member...",
}) {
  const selectedIds = Array.isArray(selectedUserIds) ? selectedUserIds : [];
  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  // Users available to be tagged (not yet tagged)
  const availableUsers = users.filter((u) => !selectedIds.includes(u.id));

  const handleAdd = (userId) => {
    if (!userId || userId === "none" || selectedIds.includes(userId)) return;
    onChange([...selectedIds, userId]);
  };

  const handleRemove = (userId) => {
    onChange(selectedIds.filter((id) => id !== userId));
  };

  return (
    <div className="space-y-1.5" data-testid="member-tag-selector">
      {label && (
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium flex items-center gap-1.5 text-foreground">
            <Users className="w-3.5 h-3.5 text-muted-foreground" />
            {label}
          </Label>
          <span className="text-[11px] text-muted-foreground font-mono">
            {selectedIds.length} tagged
          </span>
        </div>
      )}

      {/* Selected member pills */}
      <div className="flex flex-wrap gap-1.5 min-h-[30px] p-1.5 bg-muted/20 border border-border rounded-md">
        {selectedIds.map((uid) => {
          const user = userMap[uid];
          const name = user ? user.name : "Unknown User";
          return (
            <Badge
              key={uid}
              variant="secondary"
              className="pl-2 pr-1 py-0.5 text-xs flex items-center gap-1 bg-secondary/80 hover:bg-secondary text-secondary-foreground transition-colors"
            >
              <span className="truncate max-w-[140px]">{name}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(uid)}
                  className="rounded-full hover:bg-muted p-0.5 text-muted-foreground hover:text-foreground focus:outline-none"
                  title={`Remove ${name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          );
        })}

        {selectedIds.length === 0 && (
          <span className="text-xs text-muted-foreground italic px-1 py-0.5">
            No team members tagged yet
          </span>
        )}
      </div>

      {/* Dropdown to add more */}
      {!disabled && availableUsers.length > 0 && (
        <div className="mt-1">
          <Select value="none" onValueChange={handleAdd} disabled={disabled}>
            <SelectTrigger className="h-8 text-xs bg-background">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>
                {placeholder}
              </SelectItem>
              {availableUsers.map((u) => (
                <SelectItem key={u.id} value={u.id} className="text-xs">
                  {u.name} {u.role ? `(${u.role})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
